// client/src/pages/OperatorsDashboard/Token.tsx
import React, { useState, useEffect } from 'react';
import { Gift, Search, ChevronLeft, ChevronRight, Loader2, CheckCircle, XCircle, Wallet, RefreshCw, Copy, ExternalLink, Download } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import { tokenService } from '@/api/services/tokenService';
import { kxtService } from '@/api/services/kxtService';
import type { TokenSummary, TokenTransaction } from '@/api/services/tokenService';
import type { KXTWallet } from '@/api/services/kxtService';
import { useTranslation } from "react-i18next";
import { KXT_REDEMPTION_ENABLED } from "@/lib/featureFlags";

const KonnectXToken = () => {
  const { t } = useTranslation();

  // Counsel wording KX-LEG-KXT-001 (Q1–Q2) and KX-111 item 9: buy/sell/redeem
  // and KXT-as-value stay OFF for the beta. KXT is "accrued rewards" only —
  // never a balance, price or cash value. All redemption, conversion and
  // withdrawal controls below are gated behind this flag, defaulted off
  // (AGENTS.md §4: behind a flag, cut over later, delete in a separate PR).
  const kxtRedemptionEnabled = KXT_REDEMPTION_ENABLED;

  const [loading, setLoading] = useState(true);
  const [claimLoading, setClaimLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [kxtConversionLoading, setKxtConversionLoading] = useState(false);
  const [usdConversionLoading, setUsdConversionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [tokenSummary, setTokenSummary] = useState<TokenSummary | null>(null);
  const [walletInfo, setWalletInfo] = useState<KXTWallet | null>(null);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 1, totalResults: 0, resultsPerPage: 10,
  });

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawUsdAmount, setWithdrawUsdAmount] = useState('');
  const [kxtUsdRate, setKxtUsdRate] = useState<number | null>(null);
  const [conversionSource, setConversionSource] = useState<'kxt' | 'usd' | null>(null);
  const [withdrawMethod, setWithdrawMethod] = useState<'crypto' | 'bank_transfer' | 'mobile_money'>('crypto');
  const [cryptoWalletAddress, setCryptoWalletAddress] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');           // bank only
  const [accountNumber, setAccountNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileNetwork, setMobileNetwork] = useState('');
  const [mobileAccountName, setMobileAccountName] = useState(''); // separate from bank accountName
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'claim' | 'withdraw'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { fetchAllData(); }, []);

  // Refetch transactions when page or filter changes (immediate).
  // For search, debounce 400ms so we don't spam the API on every keystroke.
  useEffect(() => { fetchTransactions(); }, [currentPage, filterType]);

  useEffect(() => {
    const handle = window.setTimeout(() => { fetchTransactions(); }, 400);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchTokenSummary(), fetchTransactions(), fetchWalletInfo(), fetchKxtUsdRate()]);
    } catch (error: unknown) {
      showMessage('error', t('orgToken.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenSummary = async () => {
    try {
      const summary = await tokenService.getTokenSummary();
      setTokenSummary(summary);
      // Pre-fill wallet address if already exists
      if (summary.kxtWalletAddress) {
        setCryptoWalletAddress(summary.kxtWalletAddress);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch token summary:', error);
    }
  };

  const fetchWalletInfo = async () => {
    try {
      const wallet = await kxtService.getWallet();
      setWalletInfo(wallet);
    } catch (error: unknown) {
      console.error('Failed to fetch wallet info:', error);
    }
  };

  const fetchKxtUsdRate = async () => {
    try {
      setRateLoading(true);
      // Operator-accessible endpoint added 2026-05-12 (was admin-only before, 403'd for operators).
      // Returns { kxt, usd, usdPerKxt, formula }.
      const rateData = await kxtService.getKxtUsdRate();
      const derivedRate = Number(
        rateData?.usdPerKxt ?? rateData?.rate ?? (rateData?.usd && rateData?.kxt ? rateData.usd / rateData.kxt : NaN)
      );

      if (!Number.isFinite(derivedRate) || derivedRate <= 0) {
        throw new Error('Invalid KXT/USD rate');
      }

      setKxtUsdRate(derivedRate);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      // 404 (rate not set by admin) is expected — fall back gracefully.
      if (err?.response?.status !== 404) {
        console.error('Failed to fetch KXT/USD rate:', error);
      }
      setKxtUsdRate(null);
    } finally {
      setRateLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);
      const data = await tokenService.getTransactionHistory(currentPage, 10, filterType, searchQuery);
      setTransactions(data.transactions);
      setPagination(data.pagination);
    } catch (error: unknown) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const formatLinkedAmount = (amount: number, decimals = 6) => {
    const roundedAmount = Number(amount.toFixed(decimals));
    return Number.isFinite(roundedAmount) ? String(roundedAmount) : '';
  };

  const handleKxtAmountChange = (value: string) => {
    setConversionSource('kxt');
    setWithdrawAmount(value);
    if (!value.trim()) {
      setWithdrawUsdAmount('');
      setKxtConversionLoading(false);
    }
  };

  const handleUsdAmountChange = (value: string) => {
    setConversionSource('usd');
    setWithdrawUsdAmount(value);
    if (!value.trim()) {
      setWithdrawAmount('');
      setUsdConversionLoading(false);
    }
  };

  useEffect(() => {
    if (conversionSource !== 'kxt') return;

    if (!withdrawAmount.trim()) {
      setWithdrawUsdAmount('');
      setKxtConversionLoading(false);
      return;
    }

    const parsedAmount = Number(withdrawAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setWithdrawUsdAmount('');
      setKxtConversionLoading(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setKxtConversionLoading(true);
        const conversion = await kxtService.convertUsd(parsedAmount);
        setWithdrawUsdAmount(formatLinkedAmount(conversion.usd, 2));
      } catch (error: unknown) {
        console.error('Failed to convert KXT to USD:', error);
        setWithdrawUsdAmount('');
      } finally {
        setKxtConversionLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [conversionSource, withdrawAmount]);

  useEffect(() => {
    if (conversionSource !== 'usd') return;

    if (!withdrawUsdAmount.trim()) {
      setWithdrawAmount('');
      setUsdConversionLoading(false);
      return;
    }

    const parsedAmount = Number(withdrawUsdAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setWithdrawAmount('');
      setUsdConversionLoading(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setUsdConversionLoading(true);
        const conversion = await kxtService.convertKxtFromUsd(parsedAmount);
        setWithdrawAmount(formatLinkedAmount(conversion.kxt, 6));
      } catch (error: unknown) {
        console.error('Failed to convert USD to KXT:', error);
        setWithdrawAmount('');
      } finally {
        setUsdConversionLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [conversionSource, withdrawUsdAmount]);

  const handleClaim = async () => {
    if (!tokenSummary || tokenSummary.availableKXT <= 0) {
      showMessage('error', t('orgToken.messages.claimError'));
      return;
    }
    try {
      setClaimLoading(true);
      const response = await tokenService.claimTokens();
      if (response.success) {
        showMessage('success', response.message || t('orgToken.messages.claimSuccess'));
        await fetchTokenSummary();
        await fetchTransactions();
      } else {
        showMessage('error', response.message || t('orgToken.messages.claimError'));
      }
    } catch (error: unknown) {
      showMessage('error', (error as { response?: { data?: { message?: string } } }).response?.data?.message || t('orgToken.messages.claimError'));
    } finally {
      setClaimLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      showMessage('error', t('orgToken.messages.invalidAmount'));
      return;
    }
    if (amount < 10) {
      showMessage('error', t('orgToken.messages.minWithdrawal'));
      return;
    }
    if (!tokenSummary || amount > tokenSummary.walletBalance) {
      showMessage('error', t('orgToken.messages.insufficientBalance'));
      return;
    }

    if (withdrawMethod === 'crypto' && !cryptoWalletAddress.trim()) {
      showMessage('error', t('orgToken.messages.enterWallet'));
      return;
    }

    if (withdrawMethod === 'bank_transfer' && (!bankName.trim() || !accountName.trim() || !accountNumber.trim())) {
      showMessage('error', t('orgToken.messages.enterBankDetails'));
      return;
    }

    if (withdrawMethod === 'mobile_money' && (!mobileNetwork.trim() || !mobileNumber.trim() || !mobileAccountName.trim())) {
      showMessage('error', t('orgToken.messages.enterMobileDetails'));
      return;
    }

    try {
      setWithdrawLoading(true);
      await kxtService.submitWithdrawal({
        amount,
        withdrawalMethod: withdrawMethod,
        withdrawalDetails:
          withdrawMethod === 'bank_transfer'
            ? {
                bankName: bankName.trim(),
                accountName: accountName.trim(),
                accountNumber: accountNumber.trim(),
              }
            : withdrawMethod === 'mobile_money'
            ? {
                network: mobileNetwork.trim(),
                accountName: mobileAccountName.trim(),
                accountNumber: mobileNumber.trim(),
              }
            : {
                walletAddress: cryptoWalletAddress.trim(),
              },
      });
      // Backend queues this for admin approval. Tell the operator clearly so they
      // know to track it via the Transactions table below rather than expecting
      // instant settlement.
      showMessage(
        'success',
        t('orgToken.messages.withdrawQueued', {
          defaultValue:
            'Withdrawal submitted. It is now queued for admin approval — you can track status in your transaction history below.',
        })
      );
      setWithdrawAmount('');
      setWithdrawUsdAmount('');
      setConversionSource(null);
      if (withdrawMethod === 'bank_transfer') {
        setBankName('');
        setAccountName('');
        setAccountNumber('');
      }
      if (withdrawMethod === 'mobile_money') {
        setMobileNetwork('');
        setMobileNumber('');
        setMobileAccountName('');
      }
      await fetchTokenSummary();
      await fetchTransactions();
    } catch (error: unknown) {
      showMessage('error', (error as { response?: { data?: { message?: string } } }).response?.data?.message || t('orgToken.messages.withdrawError'));
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleGenerateWallet = async () => {
    try {
      setGenerateLoading(true);
      const result = await kxtService.generateWallet();
      showMessage('success', t('orgToken.messages.walletGenerated', { address: result.kxtWalletAddress }));
      await fetchAllData();
    } catch (error: unknown) {
      showMessage('error', (error as { response?: { data?: { message?: string } } }).response?.data?.message || t('orgToken.messages.walletGenError'));
    } finally {
      setGenerateLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showMessage('success', t('orgToken.messages.copied'));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) setCurrentPage(newPage);
  };

  const formatAmount = (amount: number) =>
    amount >= 0 ? `+${amount.toFixed(2)} KXT` : `${amount.toFixed(2)} KXT`;

  const getAmountColor = (amount: number) =>
    amount >= 0 ? 'text-green-600' : 'text-red-600';

  const formatDateTime = (value?: string | number | Date | null) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // KXT redemption is OFF for the beta (counsel, KX-LEG-KXT-001 Q1–Q2; KX-111
  // item 9). Until counsel/KX-62/KX-45 clear it, this page must not present any
  // buy/sell/redeem/convert/withdraw control, nor KXT as a balance, price or
  // cash value. Show accrued rewards (read-only) plus the transaction history.
  if (!kxtRedemptionEnabled) {
    return (
      <>
        <Helmet>
          <title>{t('orgToken.title')}</title>
          <meta name="description" content={t('orgToken.metaDescription')} />
        </Helmet>

        <div className="min-h-screen bg-gray-50 dark:bg-black" id="token-dashboard-content">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6 sm:py-8">

            {message && (
              <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
              }`}>
                {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            {/* Accrued rewards — read-only. Never a balance, price or cash value. */}
            <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Gift className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                  {t('orgToken.accruedRewardsTitle', { defaultValue: 'Accrued KXT rewards' })}
                </h2>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-200">
                {tokenSummary?.kxtTotalEarned?.toFixed(2) ?? '0.00'} KXT
              </div>
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
                {t('orgToken.accruedRewardsNotice', {
                  defaultValue:
                    'These are accrued rewards for your activity. They are not a balance, a price or a cash value, and cannot be bought, sold or redeemed.',
                })}
              </p>
            </div>

            {/* Transaction history (read-only) */}
            <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-5">
                {t('orgToken.transactionHistory')}
              </h2>
              {transactionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {t('orgToken.noTransactions')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.date')}</th>
                        <th className="text-left py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.description')}</th>
                        <th className="text-right py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-b border-gray-100">
                          <td className="py-4 px-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
                            {formatDateTime(tx.date)}
                          </td>
                          <td className="py-4 px-2 text-sm text-gray-900 dark:text-gray-300 font-medium">
                            {tx.description}
                          </td>
                          <td className={`py-4 px-2 text-sm text-right font-bold ${getAmountColor(tx.amount)}`}>
                            {formatAmount(tx.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('orgToken.title')}</title>
        <meta name="description" content={t('orgToken.metaDescription')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black" id="token-dashboard-content">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6 sm:py-8">

          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* ── Wallet Info Bar ─────────────────────────────────────── */}
          <div className="bg-white dark:bg-[#333436] rounded-xl shadow-md p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('orgToken.walletAddress')}</p>
                {tokenSummary?.kxtWalletAddress
  ? (
    <div className="flex items-center gap-2">
      <p className="text-sm font-mono text-gray-900 dark:text-gray-200">
        {tokenSummary.kxtWalletAddress.slice(0, 10)}...{tokenSummary.kxtWalletAddress.slice(-6)}
      </p>
      <button
        onClick={() => copyToClipboard(tokenSummary.kxtWalletAddress as string)}
        className="text-gray-400 hover:text-blue-500"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => window.open(`https://celo.blockscout.com/address/${tokenSummary.kxtWalletAddress}`, '_blank')}
        className="text-gray-400 hover:text-blue-500"
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </button>
    </div>
  )
  : (
    <p className="text-sm text-gray-400 dark:text-gray-500">{t('orgToken.noWallet')}</p>
  )
}
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Stats */}
              <div className="text-right hidden sm:block">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('orgToken.totalEarned')}</p>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-200">
                  {tokenSummary?.kxtTotalEarned?.toFixed(2) || '0.00'} KXT
                </p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('orgToken.totalWithdrawn')}</p>
                <p className="text-sm font-bold text-gray-900 dark:text-gray-200">
                  {tokenSummary?.kxtTotalWithdrawn?.toFixed(2) || '0.00'} KXT
                </p>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('orgToken.pending')}</p>
                <p className="text-sm font-bold text-yellow-600">
                  {tokenSummary?.kxtPendingWithdrawal?.toFixed(2) || '0.00'} KXT
                </p>
              </div>

              {/* Generate wallet button — only show if no wallet AND redemption is enabled.
                  A wallet to receive KXT withdrawals is a redemption control. */}
              {kxtRedemptionEnabled && !tokenSummary?.kxtWalletAddress && (
                <button
                  onClick={handleGenerateWallet}
                  disabled={generateLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                >
                  {generateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                  {t('orgToken.generateWallet')}
                </button>
              )}

              {/* Refresh */}
              <button
                onClick={fetchAllData}
                disabled={loading || transactionsLoading || rateLoading}
                aria-label={t('orgToken.refresh', { defaultValue: 'Refresh' })}
                className="p-2 text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${loading || transactionsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* ── Left Column ──────────────────────────────────────── */}
            <div className="space-y-6">

              {/* Token Summary */}
              <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-6">
                  {t('orgToken.tokenSummary')}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-100 rounded-lg p-5 border border-[#2c2d2f] dark:bg-[#303133]">
                    <div className="text-sm text-gray-700 mb-2 font-bold dark:text-gray-300">
                      {t('orgToken.availableKxt')}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                      {tokenSummary?.availableKXT.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {t('orgToken.pending')}: {tokenSummary?.kxtPendingWithdrawal?.toFixed(2) || '0.00'} KXT
                    </div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-5 border border-[#2c2d2f] dark:bg-[#303133]">
                    <div className="text-sm text-gray-700 mb-2 font-bold dark:text-gray-300">
                      {t('orgToken.walletBalance')}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-300">
                      {tokenSummary?.walletBalance.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {t('orgToken.onChain')}: {tokenSummary?.onChainBalance || 'N/A'}
                    </div>
                  </div>
                </div>

                {kxtRedemptionEnabled ? (
                  <button
                    onClick={handleClaim}
                    disabled={claimLoading || !tokenSummary || tokenSummary.availableKXT <= 0}
                    className="w-full bg-[#2E5A88] hover:bg-[#244a73] text-white py-4 rounded-lg font-bold text-base flex items-center justify-center space-x-2 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {claimLoading
                      ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{t('orgToken.claiming')}</span></>
                      : <><Gift className="w-5 h-5" /><span>{t('orgToken.claimTokens')}</span></>
                    }
                  </button>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {t('orgToken.accruedRewardsNotice', {
                      defaultValue:
                        'These are accrued rewards for your activity. They are not a balance, a price or a cash value, and cannot be bought, sold or redeemed.',
                    })}
                  </p>
                )}
              </div>

              {/* Transaction History */}
              <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-5">
                  {t('orgToken.transactionHistory')}
                </h2>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-3 mb-5">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('orgToken.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-[#303133] focus:border-transparent"
                    />
                  </div>
                  <select
                    value={filterType}
                    onChange={(e) => { setFilterType(e.target.value as never); setCurrentPage(1); }}
                    className="px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer dark:bg-[#303133] font-medium"
                  >
                    <option value="all">{t('orgToken.allTypes')}</option>
                    <option value="claim">{t('orgToken.claims')}</option>
                    <option value="withdraw">{t('orgToken.withdrawals')}</option>
                  </select>
                </div>

                {transactionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('orgToken.noTransactions')}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.date')}</th>
                            <th className="text-left py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.description')}</th>
                            <th className="text-right py-3.5 px-2 text-sm font-bold text-gray-700 dark:text-gray-300">{t('orgToken.amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((tx) => (
                            <tr key={tx.id} className="border-b border-gray-100">
                              <td className="py-4 px-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
                                {formatDateTime(tx.date)}
                              </td>
                              <td className="py-4 px-2 text-sm text-gray-900 dark:text-gray-300 font-medium">
                                {tx.description}
                                {tx.status === 'pending' && (
                                  <span className="ml-2 text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded">
                                    {t('orgToken.status.pending', { defaultValue: 'Pending' })}
                                  </span>
                                )}
                                {tx.status === 'completed' && (
                                  <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-600 px-2 py-0.5 rounded">
                                    {t('orgToken.status.completed', { defaultValue: 'Done' })}
                                  </span>
                                )}
                                {(tx.status as string) === 'rejected' && (
                                  <span className="ml-2 text-xs bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 px-2 py-0.5 rounded">
                                    {t('orgToken.status.rejected', { defaultValue: 'Rejected' })}
                                  </span>
                                )}
                              </td>
                              <td className={`py-4 px-2 text-sm text-right font-bold ${getAmountColor(tx.amount)}`}>
                                {formatAmount(tx.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-200">
                        <div className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                          {t('orgToken.showingResults', {
                            first: ((pagination.currentPage - 1) * pagination.resultsPerPage) + 1,
                            last: Math.min(pagination.currentPage * pagination.resultsPerPage, pagination.totalResults),
                            total: pagination.totalResults
                          })}
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 border-2 border-gray-300 rounded hover:bg-gray-50 dark:text-gray-300 disabled:opacity-50"
                          >
                            <ChevronLeft className="w-4 h-4 text-gray-600" />
                          </button>
                          {(() => {
                            // Show up to 3 page buttons, centered on currentPage,
                            // clamped within [1, totalPages].
                            const total = pagination.totalPages;
                            const windowSize = Math.min(3, total);
                            let start = Math.max(1, currentPage - 1);
                            const end = Math.min(total, start + windowSize - 1);
                            // If we are at the tail, shift start back so we always
                            // show windowSize buttons when possible.
                            if (end - start + 1 < windowSize) {
                              start = Math.max(1, end - windowSize + 1);
                            }
                            const pages: number[] = [];
                            for (let i = start; i <= end; i++) pages.push(i);
                            return pages.map((pageNum) => (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1.5 rounded text-sm font-medium min-w-[32px] ${
                                  pageNum === currentPage
                                    ? 'bg-[#2E5A88] text-white'
                                    : 'hover:bg-gray-100 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {pageNum}
                              </button>
                            ));
                          })()}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                            className="p-2 border-2 border-gray-300 rounded dark:text-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Right Column ─────────────────────────────────────── */}
            <div className="space-y-6">

              {/* Withdraw */}
              <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-6">
                  {t('orgToken.withdrawTokens')}
                </h2>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 dark:text-gray-300 mb-2">
                      {t('orgToken.amountLabel')}
                    </label>
                    <input
                      type="number"
                      placeholder={t('orgToken.amountPlaceholder')}
                      value={withdrawAmount}
                      onChange={(e) => handleKxtAmountChange(e.target.value)}
                      className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                    />
                    {tokenSummary && (
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('orgToken.availableLabel', { amount: tokenSummary.walletBalance.toFixed(2) })}
                        </p>
                        <button
                          onClick={() => handleKxtAmountChange(tokenSummary.walletBalance.toString())}
                          className="text-xs text-blue-500 font-semibold hover:text-blue-400"
                        >
                          {t('orgToken.max')}
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 dark:text-gray-300 mb-2">
                      {t('orgToken.usdEquivalent')}
                    </label>
                    <input
                      type="number"
                      placeholder={t('orgToken.usdPlaceholder')}
                      value={withdrawUsdAmount}
                      onChange={(e) => handleUsdAmountChange(e.target.value)}
                      className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {kxtConversionLoading
                        ? t('orgToken.convertingKxtToUsd')
                        : usdConversionLoading
                        ? t('orgToken.convertingUsdToKxt')
                        : rateLoading
                        ? t('orgToken.loadingRate')
                        : kxtUsdRate
                          ? `1 KXT = $${kxtUsdRate.toFixed(4)}`
                          : t('orgToken.rateUnavailable')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 dark:text-gray-300 mb-2">
                      {t('orgToken.withdrawTo')}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setWithdrawMethod('crypto');
                          // Reset method-specific fields so old data doesn't bleed across.
                          setBankName('');
                          setAccountNumber('');
                          setAccountName('');
                          setMobileAccountName('');
                          setMobileNetwork('');
                          setMobileNumber('');
                        }}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          withdrawMethod === 'crypto'
                            ? 'border-[#2E5A88] bg-[#2E5A88] text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-[#303133] dark:text-gray-300 dark:hover:bg-[#3a3b3d]'
                        }`}
                      >
                        {t('orgToken.cryptoWallet')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWithdrawMethod('bank_transfer');
                          setCryptoWalletAddress(tokenSummary?.kxtWalletAddress || '');
                          setMobileAccountName('');
                          setMobileNetwork('');
                          setMobileNumber('');
                        }}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          withdrawMethod === 'bank_transfer'
                            ? 'border-[#2E5A88] bg-[#2E5A88] text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-[#303133] dark:text-gray-300 dark:hover:bg-[#3a3b3d]'
                        }`}
                      >
                        {t('orgToken.bankTransfer')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWithdrawMethod('mobile_money');
                          setCryptoWalletAddress(tokenSummary?.kxtWalletAddress || '');
                          setBankName('');
                          setAccountName('');
                          setAccountNumber('');
                        }}
                        className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          withdrawMethod === 'mobile_money'
                            ? 'border-[#2E5A88] bg-[#2E5A88] text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-[#303133] dark:text-gray-300 dark:hover:bg-[#3a3b3d]'
                        }`}
                      >
                        {t('orgToken.mobileMoney')}
                      </button>
                    </div>

                    {withdrawMethod === 'crypto' ? (
                      <>
                        <input
                          type="text"
                          placeholder={t('orgToken.walletPlaceholder')}
                          value={cryptoWalletAddress}
                          onChange={(e) => setCryptoWalletAddress(e.target.value)}
                          className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('orgToken.walletHelp')}
                        </p>
                      </>
                    ) : withdrawMethod === 'bank_transfer' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.bankName')}
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.accountName')}
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.accountNumber')}
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <p className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                          {t('orgToken.bankHelp')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.networkPlaceholder')}
                            value={mobileNetwork}
                            onChange={(e) => setMobileNetwork(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.mobilePlaceholder')}
                            value={mobileNumber}
                            onChange={(e) => setMobileNumber(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <input
                            type="text"
                            placeholder={t('orgToken.accountName')}
                            value={mobileAccountName}
                            onChange={(e) => setMobileAccountName(e.target.value)}
                            className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400 dark:bg-[#303133]"
                          />
                        </div>
                        <p className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                          {t('orgToken.mobileHelp')}
                        </p>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleWithdraw}
                    disabled={
                      withdrawLoading ||
                      !withdrawAmount ||
                      (withdrawMethod === 'crypto'
                        ? !cryptoWalletAddress.trim()
                        : withdrawMethod === 'bank_transfer'
                        ? !bankName.trim() || !accountName.trim() || !accountNumber.trim()
                        : !mobileNetwork.trim() || !mobileNumber.trim() || !mobileAccountName.trim())
                    }
                    className="w-full bg-[#2E5A88] hover:bg-[#244a73] text-white py-4 rounded-lg font-bold transition-colors text-base mt-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {withdrawLoading
                      ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{t('orgToken.processing')}</span></>
                      : t('orgToken.withdrawButton')
                    }
                  </button>
                </div>
              </div>

              {/* Token Stats — withdrawal totals + on-chain balance frame KXT as a
                  withdrawable/on-chain asset. Gated OFF for the beta (counsel). */}
              {kxtRedemptionEnabled && (
              <div className="bg-white rounded-xl shadow-md dark:bg-[#333436] p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-300 mb-4">{t('orgToken.tokenStats')}</h3>
                <div className="space-y-3">
                  {[
                    { label: t('orgToken.totalEarned'), value: `${tokenSummary?.kxtTotalEarned?.toFixed(2) || '0.00'} KXT`, color: 'text-green-500' },
                    { label: t('orgToken.totalWithdrawn'), value: `${tokenSummary?.kxtTotalWithdrawn?.toFixed(2) || '0.00'} KXT`, color: 'text-blue-500' },
                    { label: t('orgToken.pendingWithdrawal'), value: `${tokenSummary?.kxtPendingWithdrawal?.toFixed(2) || '0.00'} KXT`, color: 'text-yellow-500' },
                    { label: t('orgToken.onChainBalance'), value: tokenSummary?.onChainBalance ? `${tokenSummary.onChainBalance} KXT` : 'N/A', color: 'text-purple-500' },
                  ].map((stat) => (
                    <div key={stat.label} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</span>
                      <span className={`text-sm font-bold ${stat.color}`}>{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KonnectXToken;
